/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createApiRef, DiscoveryApi } from '@backstage/core-plugin-api';

import { LdapApi, ClientApiConfig } from './types';

export type UserGroupsResponse = {
  groups: string[];
  err: string;
};

export type IsUserInGroupResponse = {
  isUserInGroup: boolean;
  err: string;
};

export const ldapApiRef = createApiRef<LdapApi>({
  id: 'plugin.ldap.api',
});

export class LdapClient implements LdapApi {
  static fromConfig(discovery: DiscoveryApi) {
    return new LdapClient({ discovery });
  }

  private readonly config: ClientApiConfig;

  constructor(config: ClientApiConfig) {
    this.config = config;
  }

  private getBackendBaseUrl(): Promise<string> {
    return this.config.discovery.getBaseUrl('ldap');
  }

  /**
   * Get username of user who is currently logged in
   *
   */
  getLoggedInUser(): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        const backendBaseUrl = await this.getBackendBaseUrl();
        const backendUrl = `${backendBaseUrl}/logged-in-user`;

        const response = await fetch(backendUrl);

        resolve(await response.text());
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Fetches a list of AD Groups which the user is in
   *
   */
  getGroupsForUser(user: string): Promise<string[]> {
    return new Promise<string[]>(async (resolve, reject) => {
      try {
        const backendBaseUrl = await this.getBackendBaseUrl();
        const backendUrl = `${backendBaseUrl}/user/${user}/groups`;

        const response = await fetch(backendUrl);
        const objectJson = await response.text();

        const object = JSON.parse(objectJson);
        if (object?.err) {
          reject(object.err);
        }

        resolve(object.groups);
      } catch (err) {
        reject(err?.message ?? err);
      }
    });
  }

  /**
   * Checks whether the user is in the provided AD Group
   *
   */
  isUserInGroup(user: string, group: string): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const backendBaseUrl = await this.getBackendBaseUrl();
        const backendUrl = `${backendBaseUrl}/user/${user}/groups/${group}`;

        const response = await fetch(backendUrl);
        const objectJson = await response.text();

        const object = JSON.parse(objectJson);
        if (object?.err) {
          reject(object.err);
        }

        resolve(object.isUserInGroup);
      } catch (err) {
        reject(err?.message ?? err);
      }
    });
  }
}
